// What Fleet's page and the links into it share: the tab shown and the windows
// the app's range is read over. The tab is kept for the browser tab. The
// search names the tenant's agents and models, so it stays in memory.
import { useSyncExternalStore } from "react";
import { TAB_IDS } from "./fleetOverview.js";
import { windowsOfRange } from "./overview.js";
import { hasPermission } from "./permissions.js";
import { PERMISSIONS } from "./permissionKeys.js";
import { apiSpan } from "./shell.js";

// Whether the reader can read the console: it counts sessions, so it needs
// session analytics. Without them Fleet keeps its evidence page.
export function fleetConsoleAvailable(caps) {
  return hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE) && hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ);
}

const spans = new WeakMap();
/**
 * The app's range as Fleet reads it, over the Overview's windows
 * (lib/overview.js windowsOf), so a click from one to the other reads the
 * same span: the range (`now`) and the span before it (`then`, null where the
 * two pass the widest window the analytics take), as the analytics take them,
 * and the windows themselves (`w`).
 */
export function fleetWindows(range) {
  const w = windowsOfRange(range);
  if (!spans.has(w)) spans.set(w, { w, now: apiSpan(w.now), then: w.before ? apiSpan(w.before) : null });
  return spans.get(w);
}

const TAB_KEY = "origin-fleet-console-v2";

function readTab() {
  try {
    const v = JSON.parse(globalThis.sessionStorage?.getItem(TAB_KEY) || "null");
    return { view: TAB_IDS.includes(v?.view) ? v.view : "agents" };
  } catch {
    return { view: "agents" };
  }
}

const fresh = () => readTab();
let state = fresh();
const listeners = new Set();
const emit = () => { for (const fn of listeners) fn(); };
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const read = () => state;

export function setFleet(patch) {
  const next = { ...state, ...patch };
  if (Object.keys(patch).every((k) => Object.is(state[k], next[k]))) return;
  state = next;
  if ("view" in patch) {
    try { globalThis.sessionStorage?.setItem(TAB_KEY, JSON.stringify({ view: state.view })); } catch { /* this tab only */ }
  }
  emit();
}

export function useFleet() {
  return useSyncExternalStore(subscribe, read, read);
}

export function _resetFleetForTests() {
  state = fresh();
  emit();
}
